import {
  Button,
  Dialog,
  DialogContent,
  Field,
  Input,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@lastrouter/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { Download, FilePlus2, Upload } from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { downloadUrl } from '../../api/client.js';
import { errorMessage, useApiMutation, useApiQuery } from '../../api/hooks.js';
import { useSession } from '../../app/session.js';
import { EmptyState, linkClass, PageHeader, QueryState, StatusBadge } from '../../components/common.js';
import { toBase64 } from '../../lib/files.js';
import { formatDateTime } from '../../lib/format.js';
import { HistoryView, PublishedView } from './published.js';

export type ContextTab = 'published' | 'drafts' | 'history';

export const draftTone = { aberto: 'warning', aprovado: 'success', publicado: 'neutral', descartado: 'neutral' } as const;

function NewDraftDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const catalog = useApiQuery('catalog');
  const create = useApiMutation('createDraft', ['drafts', 'overview']);
  const [reason, setReason] = useState('');
  function submit(e: FormEvent) {
    e.preventDefault();
    const snapshot = catalog.data?.snapshot ?? { formato: 1, subjects: [], prompts: [], agent_instructions: {} };
    create.mutate(
      { body: { snapshot, reason: reason.trim() } },
      {
        onSuccess: (d) => void navigate({ to: '/context/drafts/$draftId', params: { draftId: d.id } }),
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('context.drafts.newTitle')} description={t('context.drafts.newHelp')} closeLabel={t('common.close')}>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <Field id="motivo-rascunho" label={t('context.editor.reasonLabel')}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} required maxLength={500} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={!reason.trim() || create.isPending || catalog.isPending}>
              {t('context.drafts.create')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const imp = useApiMutation('importCatalog', ['drafts']);
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!file) return;
    const zip_base64 = await toBase64(file);
    imp.mutate(
      { body: { zip_base64, reason: reason.trim() } },
      {
        onSuccess: (d) => {
          toast.success(t('context.drafts.imported'));
          void navigate({ to: '/context/drafts/$draftId', params: { draftId: d.id } });
        },
        onError: (err) => toast.error(errorMessage(t, err)),
      },
    );
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('context.drafts.importTitle')} description={t('context.drafts.importHelp')} closeLabel={t('common.close')}>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <Field id="zip-contexto" label={t('context.drafts.file')}>
            <Input type="file" accept=".zip,application/zip" onChange={(e) => setFile(e.target.files?.[0] ?? null)} required className="py-1.5" />
          </Field>
          <Field id="motivo-importar" label={t('context.editor.reasonLabel')}>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} required maxLength={500} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant="primary" disabled={!file || !reason.trim() || imp.isPending}>
              {t('context.import')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DraftsView() {
  const { t } = useTranslation();
  const q = useApiQuery('drafts');
  return (
    <QueryState query={q}>
      {(drafts) =>
        drafts.length === 0 ? (
          <EmptyState>{t('context.drafts.empty')}</EmptyState>
        ) : (
          <Table>
            <TableCaption>{t('context.tabDrafts')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{t('context.drafts.reason')}</TableHead>
                <TableHead>{t('context.drafts.subjects')}</TableHead>
                <TableHead>{t('context.drafts.author')}</TableHead>
                <TableHead>{t('context.drafts.created')}</TableHead>
                <TableHead>{t('context.drafts.state')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drafts.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>
                    <Link to="/context/drafts/$draftId" params={{ draftId: d.id }} className={linkClass}>
                      {d.reason}
                    </Link>
                  </TableCell>
                  <TableCell>{d.changed_subjects.join(', ') || '-'}</TableCell>
                  <TableCell>{d.created_by}</TableCell>
                  <TableCell className="tabular whitespace-nowrap">{formatDateTime(d.created_at)}</TableCell>
                  <TableCell>
                    <StatusBadge tone={draftTone[d.status]}>{t(`context.drafts.status.${d.status}`)}</StatusBadge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      }
    </QueryState>
  );
}

export function ContextPage({ tab, onTab }: { tab: ContextTab; onTab: (t: ContextTab) => void }) {
  const { t } = useTranslation();
  const session = useSession().data;
  const catalog = useApiQuery('catalog');
  const pubs = useApiQuery('publications');
  const [dialog, setDialog] = useState<'novo' | 'importar' | null>(null);

  return (
    <>
      <PageHeader
        title={t('context.title')}
        subtitle={t('context.subtitle')}
        actions={
          <>
            {/* O zip leva o catálogo inteiro: só o admin baixa. */}
            {session?.is_admin ? (
              <Button asChild>
                <a href={downloadUrl('exportCatalog')} download="contexto.zip">
                  <Download />
                  {t('context.export')}
                </a>
              </Button>
            ) : null}
            <Button onClick={() => setDialog('importar')}>
              <Upload />
              {t('context.import')}
            </Button>
            <Button variant="primary" onClick={() => setDialog('novo')}>
              <FilePlus2 />
              {t('context.newDraft')}
            </Button>
          </>
        }
      />
      <Tabs value={tab} onValueChange={(v) => onTab(v as ContextTab)}>
        <TabsList aria-label={t('context.title')}>
          <TabsTrigger value="published">{t('context.tabPublished')}</TabsTrigger>
          <TabsTrigger value="drafts">{t('context.tabDrafts')}</TabsTrigger>
          <TabsTrigger value="history">{t('context.tabHistory')}</TabsTrigger>
        </TabsList>
        <TabsContent value="published">
          <QueryState query={catalog}>{(c) => <PublishedView snapshot={c.snapshot} />}</QueryState>
        </TabsContent>
        <TabsContent value="drafts">
          <DraftsView />
        </TabsContent>
        <TabsContent value="history">
          <QueryState query={pubs}>{(p) => <HistoryView publications={p} isAdmin={Boolean(session?.is_admin)} />}</QueryState>
        </TabsContent>
      </Tabs>
      {dialog === 'novo' ? <NewDraftDialog onClose={() => setDialog(null)} /> : null}
      {dialog === 'importar' ? <ImportDialog onClose={() => setDialog(null)} /> : null}
    </>
  );
}
